/**
 * SupervisedBanner — persistent banner rendered above every authenticated
 * route when `me-context.supervised_session !== null`.
 *
 * Per `docs/frontend/035-frontend-app-shell-plan.md §3.9` and
 * `docs/13-frontend-integration-rules.md §7`:
 *   - always visible across every route;
 *   - shows actor + subject + mode (`view_as` greys mutation buttons,
 *     `act_as` allows them through);
 *   - "End session" is intentionally absent — the mutation is deferred
 *     in MVP; the banner explains the supervisor's path forward.
 */

import { useMeContext } from '../../context/MeContextProvider';
import { ShieldIcon } from './icons';

export function SupervisedBanner() {
  const { me } = useMeContext();
  if (!me || !me.supervised_session) return null;
  const s = me.supervised_session;
  const actorName = me.profile?.full_name ?? me.profile?.email ?? 'المشرف';
  const subjectName = s.subject.full_name ?? s.subject.email ?? 'مستخدم';

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        background: s.mode === 'act_as' ? 'var(--rose-soft)' : 'var(--amber-soft)',
        color: s.mode === 'act_as' ? 'var(--rose-ink)' : 'var(--amber-ink)',
        padding: '10px 18px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        borderBottom: '1px solid var(--line-2)',
        fontSize: 12,
      }}
    >
      <span style={{ display: 'inline-flex', width: 18, height: 18 }}>
        <ShieldIcon className="" />
      </span>
      <div style={{ flex: 1, lineHeight: 1.4 }}>
        <strong>
          جلسة وصول مُشرف عليها {s.mode === 'act_as' ? '(تنفيذ نيابة)' : '(مشاهدة فقط)'}.
        </strong>{' '}
        المشرف <strong>{actorName}</strong> يعمل بالنيابة عن <strong>{subjectName}</strong>.
        {s.reason ? (
          <span style={{ color: 'var(--ink-2)', marginInlineStart: 6 }}>· السبب: {s.reason}</span>
        ) : null}
      </div>
      <span className="tiny" style={{ color: 'var(--muted)' }}>
        بدأت {new Date(s.started_at).toLocaleString('ar-SA')}
      </span>
    </div>
  );
}
